/**
 * Client for per-event seating on the kal event-day routes (ans-backend /event-day/:project_id, uuid string ids throughout):
 *
 *   GET    /events/:event_id/guests                      the event's guest list, each guest with the table they sit at
 *   PUT    /events/:event_id/guests/:guest_id/table      {table_id, allow_over_capacity?, allow_declined?}  planner or above; 409 code table_full or guest_declined
 *   DELETE /events/:event_id/guests/:guest_id/table      unseat; planner or above
 *   GET    /tables                                       the project's tables (a table with event_id set is that event's only)
 *   GET    /waiters                                      the project's waiters (never a PIN)
 *
 * Not the legacy numeric EventDayApiClient. Credentials come from a function, as in DoorApiClient.
 */
import { getApiKey, getApiUrl } from './PlanningApiClient';
import { ApiError, buildApiError } from '../utils/apiErrors';
import type { DoorCredentialSource } from './DoorApiClient';
import type { DoorGuest } from '../types/Door';
import type { SeatingTable, SeatingWaiter } from '../types/Seating';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface SeatingApi {
  listGuests(eventId: string): Promise<DoorGuest[]>;
  /** Every table of the project; the screen keeps the ones usable at the event (utils/seating usableTables). */
  listTables(): Promise<SeatingTable[]>;
  listWaiters(): Promise<SeatingWaiter[]>;
  /** Seats the guest at the table for this event. Refuses with ApiError code table_full unless allowOverCapacity, and guest_declined unless allowDeclined. */
  seat(eventId: string, guestId: string, tableId: string, opts?: { allowOverCapacity?: boolean; allowDeclined?: boolean }): Promise<DoorGuest>;
  unseat(eventId: string, guestId: string): Promise<DoorGuest>;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const int = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

export function mapTable(row: Record<string, unknown>): SeatingTable {
  return {
    id: String(row.id),
    number: int(row.number),
    name: str(row.name),
    section: str(row.section),
    capacity: int(row.capacity),
    event_id: str(row.event_id),
    waiter_id: str(row.waiter_id),
  };
}

export function mapWaiter(row: Record<string, unknown>): SeatingWaiter {
  return { id: String(row.id), name: String(row.name ?? ''), role: String(row.role ?? 'waiter'), active: row.active !== false };
}

export function createSeatingApi(credentials: DoorCredentialSource): SeatingApi {
  async function request<T>(suffix: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
    const creds = credentials();
    if (!creds) throw new ApiError('Your session has ended. Please sign in again.', { status: 401 });
    let res: Response;
    try {
      res = await fetch(`${getApiUrl()}/event-day/${creds.projectId}${suffix}`, {
        method: init.method ?? 'GET',
        headers: { 'Content-Type': 'application/json', 'x-api-key': getApiKey(), Authorization: `Bearer ${creds.token}` },
        body: init.body != null ? JSON.stringify(init.body) : undefined,
        cache: 'no-store',
      });
    } catch {
      throw ApiError.offline();
    }
    if (!res.ok) throw buildApiError(res.status, await res.json().catch(() => null));
    return res.json();
  }

  /** Ids go into the path, so only a real uuid is accepted. */
  const guestPath = (eventId: string, guestId: string): string => {
    if (!UUID.test(eventId) || !UUID.test(guestId)) throw new Error('That guest or event could not be found.');
    return `/events/${eventId}/guests/${guestId}/table`;
  };

  return {
    async listGuests(eventId) {
      if (!UUID.test(eventId)) throw new Error('That event could not be found.');
      return (await request<{ guests: DoorGuest[] }>(`/events/${eventId}/guests`)).guests;
    },
    async listTables() {
      return (await request<{ tables: Record<string, unknown>[] }>('/tables')).tables.map(mapTable);
    },
    async listWaiters() {
      return (await request<{ waiters: Record<string, unknown>[] }>('/waiters')).waiters.map(mapWaiter);
    },
    async seat(eventId, guestId, tableId, opts = {}) {
      const path = guestPath(eventId, guestId);
      const body = {
        table_id: tableId,
        ...(opts.allowOverCapacity ? { allow_over_capacity: true } : {}),
        ...(opts.allowDeclined ? { allow_declined: true } : {}),
      };
      return (await request<{ guest: DoorGuest }>(path, { method: 'PUT', body })).guest;
    },
    async unseat(eventId, guestId) {
      return (await request<{ guest: DoorGuest }>(guestPath(eventId, guestId), { method: 'DELETE' })).guest;
    },
  };
}
